import { createAsyncThunk } from "@reduxjs/toolkit";
import { CicadaAPIError } from "../../../service/errors";
import { USE_MOCK_DATA, QUOTE_FEED_MOCK, SECURITIES_MOCK } from "./config";
import { Security, QuoteFeed, ProcessedData, Data } from "./types";
import { openToast } from "../../toast/store";
import client from "../../../service/axiosClient/client";
import * as Sentry from "@sentry/react";
import PostHogService from "../../../service/PostHogService";

const processResponse = (response: any, token: string) => {
  const { securities, quote_feeds, principal } = response as Data;

  quote_feeds.unshift({ id: "none", name: "None" });

  const processedSecurities = securities.map((security: Security) => [
    security.isin,
    security.name,
    security.alias,
    security.maturity_date,
    security.security_type,
  ]);
  const processedQuoteFeeds = quote_feeds.map((quoteFeed: QuoteFeed) => [quoteFeed.name, quoteFeed.id]);

  return {
    token,
    data: {
      principal,
      securities: processedSecurities,
      quote_feeds: processedQuoteFeeds,
    } as ProcessedData,
  };
};

const processMockData = (token: string) => ({
  token,
  data: {
    principal: "Test User",
    securities: SECURITIES_MOCK,
    quote_feeds: QUOTE_FEED_MOCK,
  } as ProcessedData,
});

export const fetchData = createAsyncThunk("feedManager/fetchData", async (token: string, thunkAPI) => {
  try {
    const response = await client.get({
      url: "/feed/introspection",
    });

    const processedData = USE_MOCK_DATA ? processMockData(token) : processResponse(response, token);
    
    PostHogService.identify(processedData.data.principal, {
      authenticated_at: new Date().toISOString(),
      auth_method: 'api_token',
      data_source: USE_MOCK_DATA ? 'mock' : 'api'
    });
    
    PostHogService.trackBusinessEvent('connection', {
      status: 'success',
      endpoint: '/feed/introspection',
      mock_data_used: USE_MOCK_DATA,
    });

    thunkAPI.dispatch(
      openToast({ title: "Connected", message: "Connected to the platform API successfully", type: "success" })
    );

    return processedData;
  } catch (error) {
    const { name, message } = error as Error;

    Sentry.captureException({
      message: "Error fetching data",
      level: "error",
      data: error,
    });

    if (name === "authentication error") {
      thunkAPI.dispatch(
        openToast({ title: "Invalid API token", message: "Check token input and try again.", type: "error" })
      );
      throw new CicadaAPIError("Invalid token", "Invalid token");
    }

    thunkAPI.dispatch(openToast({ title: "Error in API", message: "Please, try again later.", type: "error" }));
    throw new CicadaAPIError("Error fetching data", message);
  }
});
